import { toast } from "@/v2-ui/toast"
import ActionMenu, {
  type ActionMenuItemProps,
} from "@/v2-backoffice-client/components/actionMenu"
import { useLicenseUpdateMutation } from "@/v2-backoffice-client/license/update/license.update.mutation"
import {
  dateInputToDatetime,
  evictLicenseQueries,
  getDateInputToday,
} from "@/v2-backoffice-client/license/license.utils"
import {
  LICENSE_STATUS_EXPIRED,
  LICENSE_STATUS_TRIAL,
} from "@/v2-backoffice-client/license/license.constants"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseEndMenuItemProps = Omit<ActionMenuItemProps, "onClick"> & {
  license: LicenseFragment
}

// Ends a license now: status Expired plus today's cancellation date. This is
// the "trial is over / access stops" path — distinct from deleting, which is
// only for rows created by mistake. The expired row remains as the record
// that a trial ran and was not converted.
function LicenseEndMenuItem(props: LicenseEndMenuItemProps) {
  const { license, ...menuItemProps } = props

  const isTrial = license.LicenseStatusCode === LICENSE_STATUS_TRIAL

  const [updateLicense, { loading, error }] = useLicenseUpdateMutation({
    update: evictLicenseQueries,
    onCompleted(data) {
      toast({
        variant: "success",
        title: isTrial ? "Trial ended" : "License ended",
        description: `${data.updateLicense.CustomerName ?? "The customer"}'s ${data.updateLicense.ProductName} license is now expired.`,
      })
    },
  })

  return (
    <ActionMenu.ConfirmItem
      iconSrc="ban"
      variant="destructive"
      isLoading={loading}
      error={error}
      errorTitle="Failed to end license"
      confirmTitle={isTrial ? "End Trial" : "End License"}
      confirmDescription={`Marks ${license.CustomerName ?? "this customer"}'s ${license.ProductName} ${isTrial ? "trial" : "license"} as expired, effective today. The row stays on the board as history.`}
      confirmText="End"
      confirmVariant="destructive"
      data-feature-code="UI_BackofficeLicense_Row_End"
      onConfirm={async () => {
        await updateLicense({
          variables: {
            id: license.Id,
            input: {
              LicenseStatusCode: LICENSE_STATUS_EXPIRED,
              CancelledAt: dateInputToDatetime(getDateInputToday()),
            },
          },
        })
      }}
      {...menuItemProps}
    >
      {isTrial ? "End Trial" : "End License"}
    </ActionMenu.ConfirmItem>
  )
}

export default LicenseEndMenuItem
